// deps
import React, { memo, useCallback, useContext, useMemo, useState } from "react";

// internal-deps
import { safeJsonParse, useEffectOnce } from "@swiggy-private/core-ui";

// helpers
import {
    getCartItemListFromBilledLineItems,
    getCouponMetaDataFromBilledItems,
    getVoucherEntityCardData,
    openHelpWebView,
} from "@dineout-payment-success-ui/helpers/index";

// types
import {
    IDineoutPaymentSuccess,
    IMetaData,
    IVoucherSkuCard,
    DineoutPaymentSuccessFlowType,
    CTA_TYPE,
} from "@dineout-payment-success-ui/interfaces";
import { CheckoutContext, closeWebview, DineoutSuccessActionPayload } from "@swiggy-private/checkout-core";
import { ExpendableType } from "@swiggy-private/dls-web-core/dist/components/ListView/types";

// constants
import { HELP_CTA_TEXT } from "@dineout-payment-success-ui/constants";

// components
import { Header } from "./components/Header";
import { Footer } from "./components/payment-success-footer";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as ArrowBack } from "@swiggy-private/dls-web-icons/dist/web/generic/arrowBack/20";
import { default as HelpIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chat/20";
import StealDealStatusBanner from "./components/BookingStatusBanner/StealDealStatusBanner";
import { CartItemListRenderer } from "@swiggy-private/dls-web-recipe-do-web/dist/components/CartItemListRenderer";
import ListView from "@swiggy-private/dls-web-core/dist/components/ListView";
import { VoucherEntityCard } from "./components/VoucherEntityCard";

// styles
import {
    AnimatedScrollableContent,
    ContentDiv,
    DineoutWrapper,
    ScrollableContent,
    HeaderTicketWrapper,
    HeaderWrapper,
    HelpIconWrapper,
    BackIconWrapper,
    HeaderRightSectionWrapper,
    DividerWrapper,
    StealDealOrderDetailsWrapper,
    ListViewWrapper,
} from "./styles";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SUCCESS_PAGE_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { getSuccessPagePresentationalMeta } from "@dineout-payment-success-ui/helpers/TableReservation";
import { useDinecashStoreAnalytics } from "@dineout-payment-success-ui/hooks/useDinecashStoreAnalytics";
import { PurchasedCouponCard } from "@swiggy-private/dls-web-recipe-do-web/dist/components/PurchasedCouponCard";
import { VoucherEntityCardSectionWrapper } from "./components/VoucherEntityCard/styles";
import {
    getPurchaseCouponListItems,
    navigateToVoucherEntity,
    redirectToNativeMyCouponsPage,
} from "../../helpers/PurchaseCouponSuccessPage.helpers";
import { CouponDetailsWrapper } from "./styles/PurchaseCouponSuccessPage.styles";

interface IPurchaseCouponSuccessPageProps extends IDineoutPaymentSuccess {
    voucherSkuCard?: IVoucherSkuCard;
    flowType?: DineoutPaymentSuccessFlowType;
    dealId?: string;
}

const PURCHASE_COUPON_SUCCESS_TEXT_MAP = {
    REDEMPTION_DETAILS: "Redemption details",
    VIEW_ALL_VOUCHERS: "View all vouchers",
};

const PurchaseCouponSuccessPage: React.FC<IPurchaseCouponSuccessPageProps> = (props) => {
    const { checkoutState } = useContext(CheckoutContext);
    const orderData = props?.data?.orders?.[0];
    const confirmOrderData = orderData?.orderJobs?.[0];
    const metaDataString = confirmOrderData?.metadata || "{}";
    const metaDataJson = safeJsonParse<IMetaData>(metaDataString);
    const [isScrolled, setIsScrolled] = useState(false);

    const isCheckoutFlow = useMemo(() => {
        return Boolean((checkoutState.payload as DineoutSuccessActionPayload)?.isCheckoutFlow);
    }, [checkoutState?.payload]);

    const billedLineItemList = useMemo(
        () => getCartItemListFromBilledLineItems(metaDataJson?.bill?.billedLineItems),
        [metaDataJson?.bill?.billedLineItems],
    );

    const couponMetaData = useMemo(
        () => getCouponMetaDataFromBilledItems(metaDataJson?.bill?.billedItems),
        [metaDataJson?.bill?.billedItems],
    );

    const voucherEntityCardData = useMemo(
        () => getVoucherEntityCardData(props?.voucherSkuCard),
        [props?.voucherSkuCard],
    );

    const {
        clickFaqTitle,
        clickHelpBtnAnalytics,
        onHelpImpression,
        onItemListImpressionFaq,
        onCouponCardImpression,
        clickCouponCode,
        onRedemptionDetailsImpression,
        clickVoucherEntityCard,
        onVoucherEntityCardImpression,
        clickDoneCta,
        onDoneCtaImpression,
    } = useDinecashStoreAnalytics(isCheckoutFlow, orderData?.orderId, props?.flowType);

    const bookingStatus = useMemo(() => confirmOrderData?.status?.toLowerCase(), [confirmOrderData?.status]);

    const bookingStatusBannerSubtitle = useMemo(
        () => metaDataJson?.ctx?.bookingStatusBannerSubtitle,
        [metaDataJson?.ctx?.bookingStatusBannerSubtitle],
    );

    const bookingStatusBannerTitle = useMemo(
        () => metaDataJson?.ctx?.bookingStatusBannerTitle?.text,
        [metaDataJson?.ctx?.bookingStatusBannerTitle],
    );

    const { ticketAttributes } = getSuccessPagePresentationalMeta(props);

    const listItems = useMemo(
        () => getPurchaseCouponListItems(ticketAttributes, clickFaqTitle),
        [ticketAttributes, clickFaqTitle],
    );

    const bookingId = useMemo(() => orderData?.orderId, [orderData]);

    const onClickBack = useCallback(() => {
        closeWebview();
    }, []);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        setIsScrolled(scrollTop > 0);
    }, []);

    const handleViewHelp = useCallback(() => {
        clickHelpBtnAnalytics();
        openHelpWebView({ orderId: orderData?.orderId || "", isCouponPurchase: true });
    }, [orderData?.orderId, clickHelpBtnAnalytics]);

    const handleCouponCardImpression = useCallback(() => {
        onCouponCardImpression(props?.dealId);
    }, [onCouponCardImpression, props?.dealId]);

    const handleCopyCouponCodeAnalytics = useCallback(() => {
        clickCouponCode({
            dealId: props?.dealId,
            statusTimestamp: couponMetaData?.statusTimestamp,
            status: couponMetaData?.status,
        });
    }, [clickCouponCode, props?.dealId, couponMetaData?.statusTimestamp, couponMetaData?.status]);

    const handleCopyCouponCode = useCallback((code: string) => {
        try {
            navigator.clipboard?.writeText(code)?.catch(() => {
                // noop
            });
        } catch {
            // noop
        }
    }, []);

    const handleVoucherEntityClick = useCallback(
        (link?: string, ctaType?: CTA_TYPE) => {
            clickVoucherEntityCard({
                title: voucherEntityCardData?.title,
                subtitle: voucherEntityCardData?.subtitle,
                ctaLink: link,
                dealId: props?.dealId,
            });
            navigateToVoucherEntity(link, ctaType);
        },
        [clickVoucherEntityCard, props?.dealId, voucherEntityCardData?.title, voucherEntityCardData?.subtitle],
    );

    const handleVoucherEntityImpression = useCallback(() => {
        onVoucherEntityCardImpression({
            title: voucherEntityCardData?.title,
            subtitle: voucherEntityCardData?.subtitle,
            ctaLink: voucherEntityCardData?.ctaLink,
            dealId: props?.dealId,
        });
    }, [
        onVoucherEntityCardImpression,
        props?.dealId,
        voucherEntityCardData?.title,
        voucherEntityCardData?.subtitle,
        voucherEntityCardData?.ctaLink,
    ]);

    useEffectOnce(() => {
        onHelpImpression();
    });

    return (
        <DineoutWrapper onScroll={handleScroll}>
            <AnimatedScrollableContent>
                <ScrollableContent skipTopPadding isApp>
                    <ContentDiv>
                        <HeaderWrapper isScrolled={isScrolled}>
                            {!isCheckoutFlow && (
                                <BackIconWrapper>
                                    <GenericIcon
                                        Icon={ArrowBack}
                                        fillColor="text_Button_Tertiary_Low"
                                        onClick={onClickBack}
                                    />
                                </BackIconWrapper>
                            )}
                            <HeaderRightSectionWrapper>
                                {!isCheckoutFlow && (
                                    <HelpIconWrapper onClick={handleViewHelp}>
                                        <GenericIcon Icon={HelpIcon} fillColor="text_Button_Tertiary_Low" />
                                        <Text variant="V2.overline.neutral.medium" color="text_Button_Tertiary_Low">
                                            {HELP_CTA_TEXT}
                                        </Text>
                                    </HelpIconWrapper>
                                )}
                            </HeaderRightSectionWrapper>
                        </HeaderWrapper>
                        {bookingStatus && (
                            <StealDealStatusBanner
                                bookingStatus={bookingStatus}
                                bookingId={bookingId}
                                bookingStatusText={bookingStatusBannerTitle}
                                bookingStatusBannerSubtitle={bookingStatusBannerSubtitle}
                                showBookingId={false}
                                showBannerSubtitle={false}
                            />
                        )}
                        <CouponDetailsWrapper>
                            <PurchasedCouponCard
                                couponMetaData={couponMetaData}
                                onCopyCode={handleCopyCouponCode}
                                onCopyCodeClickEvent={handleCopyCouponCodeAnalytics}
                                onLoadImpressionEvent={handleCouponCardImpression}
                            />
                        </CouponDetailsWrapper>
                        <VoucherEntityCardSectionWrapper>
                            <VoucherEntityCard
                                voucherEntityCardData={voucherEntityCardData}
                                onClick={handleVoucherEntityClick}
                                onLoadImpressionEvent={handleVoucherEntityImpression}
                            />
                        </VoucherEntityCardSectionWrapper>
                        <StealDealOrderDetailsWrapper data-testid={SUCCESS_PAGE_TEST_IDS.TABLE_BOOKING_INFO}>
                            <HeaderTicketWrapper>
                                <Header title={PURCHASE_COUPON_SUCCESS_TEXT_MAP.REDEMPTION_DETAILS} />
                                <CartItemListRenderer
                                    itemList={billedLineItemList}
                                    onLoadImpressionEvent={onRedemptionDetailsImpression}
                                />
                            </HeaderTicketWrapper>
                        </StealDealOrderDetailsWrapper>

                        <DividerWrapper />
                        <ListViewWrapper>
                            <ListView
                                items={listItems}
                                expendableType={ExpendableType.MULTIPLE}
                                background="canvas_Primary"
                                onItemListImpression={onItemListImpressionFaq}
                            />
                        </ListViewWrapper>
                    </ContentDiv>
                </ScrollableContent>
                <Footer
                    doneClickHandler={redirectToNativeMyCouponsPage}
                    viewHelpClickHandler={handleViewHelp}
                    showHelpCta={isCheckoutFlow}
                    customDoneCtaText={PURCHASE_COUPON_SUCCESS_TEXT_MAP.VIEW_ALL_VOUCHERS}
                    isFullWidthCta
                    doneClickEventHandler={clickDoneCta}
                    doneImpressionEventHandler={onDoneCtaImpression}
                />
            </AnimatedScrollableContent>
        </DineoutWrapper>
    );
};

export default memo(PurchaseCouponSuccessPage);
